import { useEffect, useState } from 'react'
import { Eye, EyeOff, X, Tag } from 'lucide-react'
import { useAuthStore } from '../stores'
import { isValidEmail, isStrongPassword, sanitizeHTML } from '../utils/security'
import { auth } from '../utils/backend'

type UserRole = 'scout' | 'club' | 'player'

interface AuthPageProps {
  onClose?: () => void
  onNavigate?: (view: string) => void
}

const POSITIONS = [
  { value: 'GK', label: 'Goalkeeper' },
  { value: 'CB', label: 'Centre-Back' },
  { value: 'LB', label: 'Left-Back' },
  { value: 'RB', label: 'Right-Back' },
  { value: 'CDM', label: 'Defensive Midfielder' },
  { value: 'CM', label: 'Central Midfielder' },
  { value: 'CAM', label: 'Attacking Midfielder' },
  { value: 'LM', label: 'Left Midfielder' },
  { value: 'RM', label: 'Right Midfielder' },
  { value: 'LW', label: 'Left Wing' },
  { value: 'RW', label: 'Right Wing' },
  { value: 'ST', label: 'Striker' },
  { value: 'CF', label: 'Centre-Forward' },
]

function AuthPage({ onClose, onNavigate }: AuthPageProps) {
  const { checkSession } = useAuthStore()
  const [role, setRole] = useState<UserRole>('scout')
  const [isSignUp, setIsSignUp] = useState(false)
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(() => {
    const stored = localStorage.getItem('scouta_remember_me')
    return stored === null ? true : stored !== 'false'
  })
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [playerPosition, setPlayerPosition] = useState('')
  const [playerDOB, setPlayerDOB] = useState('')
  const [playerClub, setPlayerClub] = useState('')
  const [captchaQuestion, setCaptchaQuestion] = useState({ text: '', answer: 0 })
  const [captchaInput, setCaptchaInput] = useState('')
  const [referralCode, setReferralCode] = useState('')
  const isPlayerRole = role === 'player'

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1
    const num2 = Math.floor(Math.random() * 10) + 1
    const isAddition = Math.random() > 0.5

    if (isAddition) {
      setCaptchaQuestion({ text: `What is ${num1} + ${num2}?`, answer: num1 + num2 })
    } else {
      const max = Math.max(num1, num2)
      const min = Math.min(num1, num2)
      setCaptchaQuestion({ text: `What is ${max} - ${min}?`, answer: max - min })
    }

    setCaptchaInput('')
  }

  useEffect(() => {
    if (isSignUp && !isForgotPassword) {
      generateCaptcha()
    }
  }, [isSignUp, isForgotPassword])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref') || sessionStorage.getItem('scouta:referral_code')
    if (ref) {
      setReferralCode(ref.toUpperCase())
      setIsSignUp(true)
    }
  }, [])

  const resetFormState = () => {
    setError('')
    setLoading(false)
    setPassword('')
    setCaptchaInput('')
  }

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')

    if (isForgotPassword) {
      if (!email.trim() || !isValidEmail(email)) {
        setError('Valid email is required')
        return
      }

      setLoading(true)
      try {
        await auth.resetPassword(email)
        setError('✓ Password reset email sent! Check your inbox.')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send reset email')
      } finally {
        setLoading(false)
      }
      return
    }

    if (isSignUp && !name.trim()) {
      setError('Full Name is required for signup')
      return
    }
    if (!email.trim() || !isValidEmail(email)) {
      setError('Valid email is required')
      return
    }
    if (!password) {
      setError('Password is required')
      return
    }
    if (isSignUp && !isStrongPassword(password)) {
      setError('Password must be 8+ chars with uppercase, lowercase, and numbers')
      return
    }
    if (isSignUp && isPlayerRole && !playerPosition.trim()) {
      setError('Please select your playing position')
      return
    }
    if (isSignUp && isPlayerRole && !playerDOB.trim()) {
      setError('Please enter your date of birth')
      return
    }
    if (isSignUp && !agreedToTerms) {
      setError('You must agree to the Privacy Policy and Terms & Conditions to continue')
      return
    }
    if (isSignUp && parseInt(captchaInput, 10) !== captchaQuestion.answer) {
      setError('Incorrect CAPTCHA answer. Please try again.')
      generateCaptcha()
      return
    }

    setLoading(true)
    try {
      if (isSignUp) {
        const metadata: Record<string, string> = {
          full_name: sanitizeHTML(name),
          role: role
        }
        if (isPlayerRole) {
          metadata.position = playerPosition
          metadata.date_of_birth = playerDOB
          if (playerClub.trim()) {
            metadata.club = sanitizeHTML(playerClub)
          }
        }
        if (referralCode.trim()) {
          metadata.referral_code = referralCode.trim().toUpperCase()
        }
        const { user } = await auth.signUpWithMetadata(email, password, metadata)
        
        // Create player record for player signups
        if (isPlayerRole && user) {
          try {
            const { apiClient } = await import('../utils/api')
            await apiClient.createPlayer({
              name: sanitizeHTML(name),
              age: playerDOB ? new Date().getFullYear() - new Date(playerDOB).getFullYear() : 18,
              positions: [playerPosition],
              main_position: playerPosition,
              club: playerClub.trim() || 'Free Agent',
              league: 'NPFL',
              state: '',
              lga: '',
              foot: 'Right',
              height: 0,
              weight: 0,
              goals: 0,
              assists: 0,
              apps: 0,
              shots: 0,
              kp: 0,
              dr: 0,
              du: 0,
              mins: 0,
              potential: 3,
              technical: 5,
              physical: 5,
              mental: 5,
              notes: '',
              match_desc: '',
              scout: email,
              photo_url: null,
              youtube: null,
              is_verified: false,
              is_public: true,
            })
          } catch (e) {
            console.warn('Player record creation failed:', e)
          }
        }
        
        setError('✓ Success! Check your email for a confirmation link before logging in.')
        setIsSignUp(false)
        setIsForgotPassword(false)
        setPassword('')
        setCaptchaInput('')
        setAgreedToTerms(false)
        setPlayerPosition('')
        setPlayerDOB('')
        setPlayerClub('')
      } else {
        localStorage.setItem('scouta_remember_me', rememberMe.toString())
        await auth.signIn(email, password)
        await checkSession()
        onClose?.()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const switchMode = () => {
    if (isForgotPassword) {
      setIsForgotPassword(false)
    } else {
      setIsSignUp((value) => {
        const next = !value
        if (next) generateCaptcha()
        return next
      })
      setAgreedToTerms(false)
    }
    setError('')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-3 sm:px-4">
      <div className="w-full max-w-md max-h-[95vh] overflow-y-auto rounded-[32px] border border-white/10 bg-[#07141a] p-5 sm:p-6 shadow-2xl shadow-black/40 hide-scrollbar">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#7cff01]">
              Scouta Access
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-black text-white">
              {isForgotPassword ? 'Reset Password' : isSignUp ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p className="mt-2 text-xs sm:text-sm text-gray-400">
              {isForgotPassword
                ? 'We will send a reset link to your email.'
                : isSignUp
                  ? 'Create a player, scout, or club account.'
                  : 'Sign in to continue to your dashboard.'}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-white/10 bg-white/5 p-2 text-gray-300 transition hover:bg-white/10 hover:text-white flex-shrink-0"
            aria-label="Close auth modal"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mb-4 sm:mb-5 grid grid-cols-3 gap-1.5 sm:gap-2 rounded-2xl border border-white/10 bg-black/20 p-1">
          <button
            type="button"
            onClick={() => { setRole('scout'); setIsForgotPassword(false) }}
            className={`rounded-xl px-2 sm:px-4 py-2.5 sm:py-3 text-[9px] sm:text-xs font-black uppercase tracking-widest transition ${
              role === 'scout' ? 'bg-[#7cff01] text-black' : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            Scout
          </button>
          <button
            type="button"
            onClick={() => { setRole('club'); setIsForgotPassword(false) }}
            className={`rounded-xl px-2 sm:px-4 py-2.5 sm:py-3 text-[9px] sm:text-xs font-black uppercase tracking-widest transition ${
              role === 'club' ? 'bg-[#7cff01] text-black' : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            Club/Academy
          </button>
          <button
            type="button"
            onClick={() => { setRole('player'); setIsForgotPassword(false) }}
            className={`rounded-xl px-2 sm:px-4 py-2.5 sm:py-3 text-[9px] sm:text-xs font-black uppercase tracking-widest transition ${
              role === 'player' ? 'bg-[#7cff01] text-black' : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            Player
          </button>
        </div>

        {error && (
          <div className={`mb-4 rounded-2xl border px-4 py-3 text-sm ${
            error.startsWith('✓')
              ? 'border-green-500/20 bg-green-500/10 text-green-200'
              : 'border-red-500/20 bg-red-500/10 text-red-200'
          }`}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
          {!isForgotPassword && isSignUp && (
            <>
              {isPlayerRole && (
                <div className="rounded-2xl border border-green-500/20 bg-green-500/5 p-3 sm:p-4 text-xs sm:text-sm text-green-200">
                  Create a player account to showcase your football profile, upload match clips, and connect with Scouts and Clubs.
                </div>
              )}
              <div>
                <label className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-black uppercase tracking-widest text-gray-400">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder:text-gray-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                  required
                />
              </div>
              {isPlayerRole && (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-black uppercase tracking-widest text-gray-400">Main Position</label>
                      <select
                        value={playerPosition}
                        onChange={(e) => setPlayerPosition(e.target.value)}
                        className="w-full rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                        required
                      >
                        <option value="">Select position</option>
                        {POSITIONS.map((pos) => (
                          <option key={pos.value} value={pos.value}>{pos.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-black uppercase tracking-widest text-gray-400">Date of Birth</label>
                      <input
                        type="date"
                        value={playerDOB}
                        onChange={(e) => setPlayerDOB(e.target.value)}
                        className="w-full rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-black uppercase tracking-widest text-gray-400">
                      Current Club <span className="text-gray-500">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={playerClub}
                      onChange={(e) => setPlayerClub(e.target.value)}
                      placeholder="Your current club or academy"
                      className="w-full rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder:text-gray-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                    />
                  </div>
                </>
              )}
            </>
          )}

          {isSignUp && (
            <div>
              <label className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-black uppercase tracking-widest text-gray-400">
                Referral Code <span className="text-gray-500">(optional)</span>
              </label>
              <div className="relative">
                <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="Enter referral code"
                  maxLength={6}
                  className="w-full rounded-2xl border border-white/10 bg-black/30 pl-9 pr-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder:text-gray-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                />
              </div>
            </div>
          )}

          <div>
            <label className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-black uppercase tracking-widest text-gray-400">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder:text-gray-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
              required
            />
          </div>

          {!isForgotPassword && (
            <div>
              <label className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-black uppercase tracking-widest text-gray-400">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 pr-10 sm:pr-12 text-sm sm:text-base text-white placeholder:text-gray-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 sm:px-4 text-gray-400 hover:text-white"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          )}

          {!isForgotPassword && !isSignUp && (
            <label className="flex items-center gap-3 text-xs sm:text-sm text-gray-300">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-[#7cff01] focus:ring-[#7cff01]"
              />
              Remember me
            </label>
          )}

          {!isForgotPassword && isSignUp && (
            <div>
              <label className="mb-1.5 sm:mb-2 block text-[10px] sm:text-xs font-black uppercase tracking-widest text-gray-400">CAPTCHA</label>
              <div className="space-y-2 sm:space-y-3">
                <div className="rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white font-bold tracking-widest select-none">
                  {captchaQuestion.text}
                </div>
                <input
                  type="number"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  placeholder="Answer"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder:text-gray-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                  required
                />
              </div>
            </div>
          )}

          {!isForgotPassword && isSignUp && (
            <label className="flex items-start gap-2.5 sm:gap-3 text-xs sm:text-sm text-gray-300">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-800 text-[#7cff01] focus:ring-[#7cff01]"
              />
              <span className="leading-relaxed">
                I agree to Scouta&apos;s{' '}
                <button
                  type="button"
                  onClick={() => { onClose?.(); onNavigate?.('privacy') }}
                  className="text-[#7cff01] underline hover:text-[#8eff1a]"
                >
                  Privacy Policy
                </button>{' '}
                and{' '}
                <button
                  type="button"
                  onClick={() => { onClose?.(); onNavigate?.('terms') }}
                  className="text-[#7cff01] underline hover:text-[#8eff1a]"
                >
                  Terms & Conditions
                </button>
                .
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-2xl bg-[#7cff01] px-4 py-2.5 sm:py-3 font-black uppercase tracking-widest text-sm sm:text-base text-black transition hover:bg-[#8eff1a] disabled:cursor-not-allowed disabled:bg-gray-700 disabled:text-gray-400"
          >
            {loading
              ? 'Processing...'
              : isForgotPassword
                ? 'Send Reset Link'
                : isSignUp
                  ? 'Sign Up'
                  : `Login as ${role === 'scout' ? 'Scout' : role === 'club' ? 'Club/Academy' : 'Player'}`}
          </button>

          <button
            type="button"
            onClick={switchMode}
            className="w-full text-xs sm:text-sm text-gray-400 underline transition hover:text-[#7cff01]"
          >
            {isForgotPassword
              ? 'Back to Login'
              : isSignUp
                ? 'Already have an account? Login'
                : 'Need an account? Sign Up'}
          </button>

          {!isForgotPassword && !isSignUp && (
            <button
              type="button"
              onClick={() => { resetFormState(); setIsForgotPassword(true) }}
              className="w-full text-xs sm:text-sm text-gray-400 underline transition hover:text-[#7cff01]"
            >
              Forgot your password?
            </button>
          )}
        </form>
      </div>
    </div>
  )
}

export default AuthPage
